import React, { useEffect, useState } from "react";
import { ShieldCheck, FileText, Download, Sparkles, X, ArrowRight, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

export default function Assurances() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ property_type: "Appartement", surface: 50, coverage_amount: 30000 });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/insurance/marketplace").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const contactInsurer = async (p) => {
    try {
      const res = await api.post("/conversations/start", { name: p.insurer_name || "Assureur", subject: `Bonjour, j'ai une question sur votre offre « ${p.name} ».` });
      toast.success(res.data.existing ? "Conversation existante ouverte" : `Conversation démarrée avec ${p.insurer_name}`);
      navigate("/app/messagerie");
    } catch (e) { toast.error("Impossible de démarrer la conversation"); }
  };

  const openQuote = (p) => { setActive(p); setQuote(null); setForm({ property_type: "Appartement", surface: 50, coverage_amount: 30000 }); };

  const getQuote = async () => {
    setLoading(true);
    try {
      const res = await api.post("/insurance/quote", { product_id: active.id, ...form, surface: Number(form.surface), coverage_amount: Number(form.coverage_amount) });
      setQuote(res.data);
    } catch (e) { toast.error("Erreur lors du calcul du devis"); } finally { setLoading(false); }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      const res = await api.post("/insurance/subscribe", {
        product_id: active.id, ...form, surface: Number(form.surface), coverage_amount: Number(form.coverage_amount),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error("Erreur lors de la souscription"); setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-brand-greenDeep p-6 text-white relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-brand-lime/20 rounded-full blur-2xl" />
        <div className="relative">
          <h2 className="font-display text-2xl font-semibold">Marketplace d'assurance</h2>
          <p className="text-white/70 mt-1 text-sm">Comparez les offres, obtenez un devis instantané et souscrivez en ligne.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl p-6 border border-brand-soft hover:shadow-xl transition-all flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-brand-greenDeep" /></span>
              <div>
                <h3 className="font-semibold text-brand-greenDeep">{p.name}</h3>
                <p className="text-xs text-brand-green/50">{p.type} · {p.insurer_name}</p>
              </div>
            </div>
            <p className="text-sm text-brand-green/60 flex-1">{p.description}</p>
            <div className="mt-4 space-y-1 text-sm">
              <p className="text-brand-green/70"><span className="font-medium text-brand-greenDeep">Couverture :</span> {p.coverage}</p>
              <p className="text-brand-green/70"><span className="font-medium text-brand-greenDeep">À partir de :</span> {p.base_price} €/an</p>
            </div>
            {p.docs && p.docs.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {p.docs.map((d) => (
                  <a key={d.doc_id} href={`${API}/insurance/documents/${d.doc_id}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-brand-softer text-brand-green hover:bg-brand-soft transition-colors">
                    <FileText className="w-3.5 h-3.5" /> {d.filename?.slice(0, 18) || "Document"}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-5 flex gap-2">
              <button onClick={() => openQuote(p)} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> Obtenir un devis
              </button>
              <button onClick={() => contactInsurer(p)} className="px-4 py-3 rounded-full border border-brand-soft text-brand-green font-medium text-sm hover:bg-brand-softer transition-colors inline-flex items-center gap-2" title="Contacter l'assureur" data-testid={`contact-insurer-${p.id}`}>
                <MessageSquare className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {products.length === 0 && <p className="col-span-full text-center py-16 text-brand-green/50">Aucune offre disponible pour le moment.</p>}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setActive(null)}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">Devis — {active.name}</h3>
              <button onClick={() => setActive(null)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-brand-green/50 mb-5">{active.insurer_name}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Type de bien</label>
                <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="input">
                  {["Appartement", "Maison", "Commerce", "Parking", "Bureau"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Surface (m²)</label>
                  <input type="number" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} className="input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Montant garanti (€)</label>
                  <input type="number" value={form.coverage_amount} onChange={(e) => setForm({ ...form, coverage_amount: e.target.value })} className="input" />
                </div>
              </div>

              {quote ? (
                <div className="rounded-2xl bg-brand-softer p-5 text-center">
                  <p className="text-sm text-brand-green/60">Votre devis personnalisé</p>
                  <p className="font-display text-4xl font-bold text-brand-greenDeep mt-1">{quote.annual_amount} €<span className="text-base font-normal text-brand-green/50">/an</span></p>
                  <p className="text-sm text-brand-green/60">soit {quote.monthly_amount} €/mois</p>
                  <button onClick={subscribe} disabled={loading} className="mt-4 w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {loading ? "Redirection..." : "Souscrire et payer"} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[11px] text-brand-green/40 mt-2">Paiement sécurisé via Stripe · carte test 4242 4242 4242 4242</p>
                </div>
              ) : (
                <button onClick={getQuote} disabled={loading} className="w-full py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold transition-colors disabled:opacity-60">
                  {loading ? "Calcul..." : "Calculer mon devis"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;padding:0.7rem 1rem;border-radius:1rem;border:1px solid #efe7d6;outline:none;transition:all .2s}.input:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.25)}`}</style>
    </div>
  );
}
